import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Group, Modal, Text } from "@mantine/core";
import { useDisclosure, useIdle, useSessionStorage } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import i18n from "src/i18n";
import { useShallow } from "zustand/react/shallow";

import { ErrorPage } from "@pages/ErrorPage";

import { AppLoader } from "@components/AppLoader";

import { serverPulseCheck } from "@utils/api";
import { showInfoNotification } from "@utils/showNotification";

import { IDLE_SIGN_OUT_DELAY, IDLE_WARNING_DELAY } from "@constants/index";

import { useSignOut } from "@hooks/mutations/user/useSignOut";
import { useTelemetryStore } from "@hooks/store/useTelemetryStore";

import { useAuth } from "@contexts/auth";


interface AuthGuardProps {
  children: React.ReactElement;
  allowedRoles: string[];
  enforceAllowedRoles?: boolean;
  blockedRoles?: string[];
}

export const AuthGuard = ({ children, allowedRoles, enforceAllowedRoles = false, blockedRoles = [] }: AuthGuardProps) => {
  const { t } = useTranslation();
  const { token, roles, hasRequestedTokenRefresh, onboardingStatus, canAccessPath, permissionsLoading } = useAuth();
  const [
    setTotalTimeSpent,
    setIdleWarningCount,
    setIdleTime,
    setIdleSignOutCount,
  ] = useTelemetryStore(
    useShallow(state => [
      state.setTotalTimeSpent,
      state.setIdleWarningCount,
      state.setIdleTime,
      state.setIdleSignOutCount,
    ])
  );
  // const [videoCallThreshold] = useSessionStorage({
  //   key: SESSION_STORAGE_KEYS.VC_THRESHOLD,
  // });
  const { pathname, search: queryParams } = useLocation();
  // console.log(pathname, search);

  const isIdle = useIdle(IDLE_WARNING_DELAY, { initialState: false });
  const [idleModalOpened, { open: openIdleModal, close: closeIdleModal }] =
    useDisclosure(false);
  const [isLoading, setIsLoading] = useState(true);
  // console.log(isMounted);

  const { mutate: signOut } = useSignOut();

  const canAccessPage = canAccessPath(pathname) &&
    (!enforceAllowedRoles || allowedRoles.some(role => roles.includes(role))) &&
    (roles.includes('admin') || !blockedRoles.some(role => roles.includes(role)));
  
  // Intercept doctors who are not fully approved
  const isDoctorOnboarding = roles.includes('doctor') && onboardingStatus && onboardingStatus !== 'approved';

  useEffect(() => {
    let timeout: NodeJS.Timer | undefined;

    // In auth guard, start recording time spent
    const totalTimeSpentInterval: NodeJS.Timer = setInterval(() => {
      setTotalTimeSpent(prev => prev + 1);
    }, 1000 * 1);

    if (isIdle) {
      import.meta.env.DEV && console.log("User is idle");
      setIdleWarningCount(prev => prev + 1);
      // prettier-ignore
      setIdleTime(prev => prev + (IDLE_WARNING_DELAY / 1000)); // Convert idle warning delay to seconds
      openIdleModal();

      timeout = setTimeout(() => {
        closeIdleModal();
        showInfoNotification({
          message: i18n.t("idle-signout-reason"),
          autoClose: false,
        });
        sessionStorage.clear();
        modals.closeAll();
        setIdleSignOutCount(prev => prev + 1);
        setIdleTime(prev => prev + IDLE_SIGN_OUT_DELAY / 1000); // Convert idle sign out delay to seconds
        signOut({ redirectUrl: pathname + queryParams });
      }, IDLE_SIGN_OUT_DELAY);
    } else {
      import.meta.env.DEV && console.log("User is not idle");
      clearTimeout(timeout);
    }

    return () => {
      clearInterval(totalTimeSpentInterval);
      clearTimeout(timeout);
    };
  }, [isIdle]);

  useEffect(() => {
    // console.log(isMounted);

    if (token) {
      setIsLoading(false);
    } else if (hasRequestedTokenRefresh) {
      // Token initialization complete but no token found
      setIsLoading(false);
      serverPulseCheck();
    }
    // Otherwise, still waiting for initialization
  }, [token, hasRequestedTokenRefresh]);

  if (isLoading || (token && permissionsLoading)) {
    return <AppLoader />;
  } else if (isDoctorOnboarding && !pathname.startsWith('/onboard')) {
    return <Navigate to="/onboard" replace />;
  } else if (
    token &&
    canAccessPage
    //  || import.meta.env.DEV
  ) {
    return (
      <>
        <Modal
          opened={idleModalOpened}
          onClose={closeIdleModal}
          zIndex={999999} // Should be highest z-index
          centered
          title={<Text fw={500}>{t("idle-title")}</Text>}
          styles={{ body: { minHeight: "auto" } }}>
          <Text size="sm" lh={1.5}>
            {t("idle-message")}
          </Text>
          <Group justify="end" mt="xl">
            <Button onClick={closeIdleModal}>{t("im-still-here")}</Button>
          </Group>
        </Modal>
        {children}
      </>
    );
  } else if (
    token &&
    !canAccessPage // Needed to redirect to error page
  ) {
    return <ErrorPage reason="unauthorized" />;
  } else if (hasRequestedTokenRefresh && !token) {
    return <Navigate to="/" state={{ redirectURL: pathname + queryParams }} />;
  }
};
